import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { getClients, createClient, updateClient, deleteClient } from '../services/api';

const EMPTY = { client_number:'', name:'', name_ar:'', email:'', phone:'', civil_id:'', company:'', project_name:'', address:'', notes:'' };

function printClients(clients, total) {
  const win = window.open('', '_blank');
  const rows = clients.map(c=>'<tr><td>'+c.client_number+'</td><td style="font-weight:600">'+c.name+'</td><td>'+(c.civil_id||'--')+'</td><td>'+c.email+'</td><td>'+(c.phone||'--')+'</td><td>'+(c.company||'--')+'</td><td>'+(c.project_name||'--')+'</td></tr>').join('');
  win.document.write('<!DOCTYPE html><html><head><title>Clients Report</title><style>body{font-family:Arial,sans-serif;padding:30px;color:#111}.header{display:flex;justify-content:space-between;border-bottom:2px solid #00b368;padding-bottom:16px;margin-bottom:20px}.company{font-size:22px;font-weight:700;color:#00b368}table{width:100%;border-collapse:collapse;font-size:12px}th{background:#f5f5f5;padding:9px 10px;text-align:left;font-size:10px;text-transform:uppercase;border-bottom:2px solid #eee;color:#666}td{padding:9px 10px;border-bottom:1px solid #f0f0f0}.footer{margin-top:30px;text-align:center;font-size:11px;color:#aaa;border-top:1px solid #eee;padding-top:12px}</style></head><body><div class="header"><div><img src="https://teknulugy.online/logo.svg" style="height:38px;width:auto" alt="Teknulugy"/><div style="font-size:12px;color:#666">IT Services - Kuwait</div></div><div style="text-align:right"><div style="font-size:18px;font-weight:700">Clients Report</div><div style="font-size:12px;color:#666">Generated: '+new Date().toLocaleDateString()+'</div><div style="font-size:12px;color:#666">Total: '+total+' clients</div></div></div><table><thead><tr><th>#</th><th>Name</th><th>Civil ID</th><th>Email</th><th>Phone</th><th>Company</th><th>Project</th></tr></thead><tbody>'+rows+'</tbody></table><div class="footer">Teknulugy IT Services - Ahmed Al Jaber Street, Baitak Tower, Floor 1, Office D - +965 55107000</div></body></html>');
  win.document.close();
  setTimeout(()=>win.print(),500);
}

function ClientForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState(initial || EMPTY);
  const set = (k,v) => setForm(p=>({...p,[k]:v}));
  const handleSave = () => {
    if (!form.name || !form.email) return toast.error('Name and email are required');
    onSave(form);
  };
  return (
    <div className="card" style={{marginBottom:16}}>
      <div className="form-section-title">{initial ? 'Edit Client' : 'New Client'}</div>
      <div className="form-grid">
        <div className="form-group"><label className="form-label">Client Number</label>
          <input className="form-input" value={form.client_number} onChange={e=>set('client_number',e.target.value)} placeholder="Auto-generated if empty"/></div>
        <div className="form-group"><label className="form-label">Full Name *</label>
          <input className="form-input" value={form.name} onChange={e=>set('name',e.target.value)} placeholder="Client full name"/></div>
        <div className="form-group"><label className="form-label">الاسم بالعربي</label>
          <input className="form-input" value={form.name_ar||''} onChange={e=>set('name_ar',e.target.value)} placeholder="الاسم الكامل بالعربية" dir="rtl" style={{textAlign:'right'}}/></div>
        <div className="form-group"><label className="form-label">Email *</label>
          <input className="form-input" type="email" value={form.email} onChange={e=>set('email',e.target.value)} placeholder="info@company.com"/></div>
        <div className="form-group"><label className="form-label">Phone</label>
          <input className="form-input" value={form.phone} onChange={e=>set('phone',e.target.value)} placeholder="+965 XXXX XXXX"/></div>
        <div className="form-group"><label className="form-label">Civil ID</label>
          <input className="form-input" value={form.civil_id} onChange={e=>set('civil_id',e.target.value)} placeholder="Civil ID number"/></div>
        <div className="form-group"><label className="form-label">Company</label>
          <input className="form-input" value={form.company} onChange={e=>set('company',e.target.value)} placeholder="Company name"/></div>
        <div className="form-group"><label className="form-label">Project Name</label>
          <input className="form-input" value={form.project_name} onChange={e=>set('project_name',e.target.value)} placeholder="e.g. E-Commerce Platform"/></div>
        <div className="form-group"><label className="form-label">Address</label>
          <input className="form-input" value={form.address} onChange={e=>set('address',e.target.value)} placeholder="Kuwait City, Kuwait"/></div>
        <div className="form-group" style={{gridColumn:'1/-1'}}><label className="form-label">Notes</label>
          <textarea className="form-textarea" value={form.notes} onChange={e=>set('notes',e.target.value)} placeholder="Additional notes..."/></div>
      </div>
      <div style={{display:'flex',gap:8,marginTop:4}}>
        <button className="btn btn-primary" onClick={handleSave}>Save Client</button>
        <button className="btn btn-outline" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

export default function ClientsPage() {
  const qc = useQueryClient();
  const [search, setSearch]     = useState('');
  const [page, setPage]         = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing]   = useState(null);
  const limit = 10;

  const { data, isLoading } = useQuery({
    queryKey: ['clients', search, page],
    queryFn: () => getClients({ search, page, limit }).then(r => r.data),
    keepPreviousData: true,
  });

  const createMut = useMutation({
    mutationFn: createClient,
    onSuccess: () => { qc.invalidateQueries(['clients']); setShowForm(false); toast.success('Client created'); },
    onError: e => toast.error(e.response?.data?.message || 'Error'),
  });
  const updateMut = useMutation({
    mutationFn: ({id,data}) => updateClient(id,data),
    onSuccess: () => { qc.invalidateQueries(['clients']); setEditing(null); toast.success('Updated'); },
    onError: e => toast.error(e.response?.data?.message || 'Error'),
  });
  const deleteMut = useMutation({
    mutationFn: deleteClient,
    onSuccess: () => { qc.invalidateQueries(['clients']); toast.success('Client removed'); },
  });

  const clients    = data?.data  || [];
  const total      = data?.total || 0;
  const totalPages = Math.ceil(total / limit);

  const handleSearch = (val) => { setSearch(val); setPage(1); };

  return (
    <div>
      <div className="g4" style={{marginBottom:20}}>
        <div className="card-sm"><div className="stat-label">Total Clients</div><div className="stat-val">{total}</div></div>
        <div className="card-sm"><div className="stat-label">This Page</div><div className="stat-val">{clients.length}</div></div>
        <div className="card-sm"><div className="stat-label">Current Page</div><div className="stat-val">{page} / {totalPages||1}</div></div>
        <div className="card-sm"><div className="stat-label">Per Page</div><div className="stat-val">{limit}</div></div>
      </div>

      <div style={{display:'flex',gap:10,marginBottom:16,flexWrap:'wrap',alignItems:'center'}}>
        <input className="search-input" placeholder="Search by name, email, civil ID, company..." value={search} onChange={e=>handleSearch(e.target.value)} style={{width:320}}/>
        <div style={{marginLeft:'auto',display:'flex',gap:8}}>
          <button className="btn btn-outline" onClick={()=>printClients(clients,total)} style={{display:'flex',alignItems:'center',gap:6}}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
            Print
          </button>
          <button className="btn btn-primary" onClick={()=>{setShowForm(true);setEditing(null);}}>+ Add Client</button>
        </div>
      </div>

      {showForm && !editing && <ClientForm onSave={d=>createMut.mutate(d)} onCancel={()=>setShowForm(false)}/>}
      {editing && <ClientForm initial={editing} onSave={d=>updateMut.mutate({id:editing.id,data:d})} onCancel={()=>setEditing(null)}/>}

      <div className="card">
        {isLoading ? <div className="loading-center"><div className="spinner"/></div> : (
          <>
            <table className="tbl">
              <thead>
                <tr><th>Client</th><th>Number</th><th>Civil ID</th><th>Email</th><th>Phone</th><th>Company</th><th>Project</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {clients.length===0 && <tr><td colSpan={8} className="empty-state">No clients found</td></tr>}
                {clients.map(c=>(
                  <tr key={c.id}>
                    <td>
                      <div style={{display:'flex',alignItems:'center',gap:10}}>
                        <div className="avatar">{c.name.slice(0,2).toUpperCase()}</div>
                        <div>
                          <div style={{fontWeight:600}}>{c.name}</div>
                          {c.address&&<div style={{fontSize:11,color:'var(--muted)'}}>{c.address}</div>}
                        </div>
                      </div>
                    </td>
                    <td style={{fontFamily:'monospace',fontSize:12,color:'var(--muted)'}}>{c.client_number}</td>
                    <td style={{fontFamily:'monospace',fontSize:12,color:'var(--muted)'}}>{c.civil_id||'--'}</td>
                    <td style={{color:'var(--muted)'}}>{c.email}</td>
                    <td style={{color:'var(--muted)'}}>{c.phone||'--'}</td>
                    <td style={{color:'var(--muted)'}}>{c.company||'--'}</td>
                    <td>{c.project_name?<span className="badge badge-info">{c.project_name}</span>:'--'}</td>
                    <td>
                      <div style={{display:'flex',gap:6}}>
                        <button className="btn btn-outline btn-sm" onClick={()=>{setEditing(c);setShowForm(false);}}>Edit</button>
                        <button className="btn btn-danger btn-sm" onClick={()=>{if(window.confirm('Deactivate?'))deleteMut.mutate(c.id);}}>Remove</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {totalPages > 1 && (
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginTop:16,paddingTop:16,borderTop:'1px solid var(--border)'}}>
                <div style={{fontSize:13,color:'var(--muted)'}}>
                  Showing <strong>{((page-1)*limit)+1}</strong> to <strong>{Math.min(page*limit,total)}</strong> of <strong>{total}</strong> clients
                </div>
                <div style={{display:'flex',gap:6,flexWrap:'wrap',alignItems:'center'}}>
                  <button className="btn btn-outline btn-sm" onClick={()=>setPage(1)} disabled={page===1}>«</button>
                  <button className="btn btn-outline btn-sm" onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page===1}>‹ Prev</button>
                  {Array.from({length:totalPages},(_,i)=>i+1)
                    .filter(p=>p===1||p===totalPages||Math.abs(p-page)<=2)
                    .reduce((acc,p,idx,arr)=>{
                      if(idx>0&&arr[idx-1]!==p-1) acc.push('...');
                      acc.push(p);
                      return acc;
                    },[])
                    .map((p,idx)=>
                      p==='...'
                        ? <span key={idx} style={{padding:'0 4px',color:'var(--muted)'}}>...</span>
                        : <button key={p} className={"btn btn-sm "+(page===p?'btn-primary':'btn-outline')} onClick={()=>setPage(p)}>{p}</button>
                    )
                  }
                  <button className="btn btn-outline btn-sm" onClick={()=>setPage(p=>Math.min(totalPages,p+1))} disabled={page===totalPages}>Next ›</button>
                  <button className="btn btn-outline btn-sm" onClick={()=>setPage(totalPages)} disabled={page===totalPages}>»</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
